import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import produce from 'immer';
import _ from 'lodash';
import { Button, Icon, Image } from 'semantic-ui-react';
import styled from 'styled-components/macro';
import { errorActions } from '../app/error-slice';
import { DEFAULT_FORM_VALUE as emptyString } from '../common/constants';
import { VerticalStepper } from '../components/common/vertical-stepper';
import { ImageContainer } from '../components/tot-pay/tot-payment-styles';
import { strRegistrationApiSender } from '../utils/str-registration-api-sender';
import { BackContinueButton } from '../widgets/back-continue-button';
import { RevenueInput } from './revenue-input';
import { RevenueModal } from './revenue-modal';

function taxablePeriodsToPayload(taxablePeriods) {
  const paymentPeriods = Object.keys(taxablePeriods)
    .map((year) => taxablePeriods[year]
      .filter((period) => period.checked && Boolean(period.grossRevenue) && Boolean(period.grossExemptRevenue))
      .map((period) => ({
        dateStart: period.makeDateStart(year),
        dateEnd: period.makeDateEnd(year),
        grossRevenue: Number(period.grossRevenue),
        grossExemptRevenue: Number(period.grossExemptRevenue),
      }))
    )
    .flat();
  return { paymentPeriods };
}

const grossExemptRevenueError = '"Gross Exempt Revenue" cannot be greater than "Gross Revenue"';
const grossRevenueKey = 'grossRevenue';
const grossExemptRevenueKey = 'grossExemptRevenue';
const grossExemptRevenueErrorKey = 'grossExemptRevenueError';

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const InfoParagraph = styled.div`
  font-family: Lato;
  font-size: 20px;
  line-height: 1.56;
  color: #5c5d5d;
  margin-bottom: 2em;
`;

export function GrossRevenueForm(props) {
  const {
    businessInfo,
    setLicenseFeeInfo,
    taxablePeriods,
    setTaxablePeriods
  } = props;

  const history = useHistory();
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const updateTaxablePeriods = useCallback((path, value) => {
    const nextTaxablePeriods = produce(taxablePeriods, (draftState) => {
      _.set(draftState, path, value);

      const [period, key] = path.split('.');

      const currentGR =
        key === grossRevenueKey
          ? value
          : Number(_.get(taxablePeriods, `${period}.${grossRevenueKey}`));

      const currentGER =
        key === grossExemptRevenueKey
          ? value
          : Number(_.get(taxablePeriods, `${period}.${grossExemptRevenueKey}`));

      _.set(
        draftState,
        `${period}.${grossExemptRevenueErrorKey}`,
        currentGER > currentGR ? grossExemptRevenueError : emptyString
      );
    });

    setTaxablePeriods(nextTaxablePeriods);
  }, [taxablePeriods, setTaxablePeriods]);


  const continueClickCB = useCallback(async() => {
    const payload = taxablePeriodsToPayload(taxablePeriods);

    try {
      setIsLoading(true);
      const { data } = await strRegistrationApiSender
        .post(`/calculateBusinessLicenseFee/${businessInfo.licenseId}`, payload);

      setIsLoading(false);
      setLicenseFeeInfo(data);
      history.push('/confirmation');
    } catch (err) {
      Sentry.captureException(err);
      setIsLoading(false);

      dispatch(errorActions.setHasErrored(Boolean(err)));
    }
  }, [history, taxablePeriods]);

  const continueDisabled = useMemo(() => {
    const filledAllValuesAndNoErrors = Object.values(taxablePeriods)
      .flat()
      .filter((item) => item.checked)
      .every((item) => {
        const hasGrossRevenue = Boolean(item.grossRevenue);
        const hasGrossExemptRevenue = Boolean(item.grossExemptRevenue);
        const hasGrossExemptRevenueError = Boolean(item.grossExemptRevenueError.length);

        return hasGrossRevenue && hasGrossExemptRevenue && !hasGrossExemptRevenueError
      });

    return !filledAllValuesAndNoErrors;
  }, [taxablePeriods]);

  return (
    <>
      <VerticalStepper activeStep={1}>
        <Column>
          <InfoParagraph>
            For the following reporting period, please report the “Total Gross Revenues”. <br />
            “Total Gross Revenues” is defined as “Total Gross Revenues Charged” less “Total Exempt Gross Revenues”. <br />
            “Total Gross Revenues Charged” shall not include federal, state or local taxes collected. <br />
            “Total Exempt Gross Revenues” includes gas, propane, fishing licenses, service charges, non-taxable food items.
          </InfoParagraph>

          <div>
            {Object.keys(taxablePeriods)
              .reverse()
              .map((year) => {
                const anyChecked = taxablePeriods[year].some((item) => item.checked);

                return anyChecked ? (
                  <RevenueInput
                    key={year}
                    year={year}
                    periods={taxablePeriods[year]}
                    setPeriodInputValue={updateTaxablePeriods}
                  />
                ) : null;
              })}

            <Button
              css="margin-top: 20px;"
              onClick={() => setModalOpen(true)}
              style={{ color: "#5c5d5d", backgroundColor: "#e0e1e2" }}
            >
              <Icon name="add" />
              <span>Add additional quarters</span>
            </Button>

            <BackContinueButton
              onContinueClick={continueClickCB}
              continueDisabled={continueDisabled}
              isLoading={isLoading}
            />
          </div>

          <ImageContainer>
            <Image src="../../assets/undraw-checking-boxes-2-ibd.svg" />
          </ImageContainer>
        </Column>
      </VerticalStepper>

      <RevenueModal
        modalOpen={modalOpen}
        taxablePeriods={taxablePeriods}
        updateTaxablePeriods={updateTaxablePeriods}
        setModalOpen={setModalOpen}
      />
    </>
  );
}
